import {
  ChecklistIcon,
  CommentDiscussionIcon,
  CopilotIcon,
  FileDiffIcon,
  GitCommitIcon,
  ShieldIcon,
  PulseIcon,
} from '@primer/octicons-react'
import {CounterLabel, UnderlineNav} from '@primer/react'
import {TabNav} from '@primer/react/deprecated'
import {Link} from '@github-ui/react-core/link'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {clsx} from 'clsx'
import type {NavigationCounterPageData} from '../page-data/payloads/tab-counts'
import {pullRequestsLayoutRoute} from '../routes/layout-route'
import type {NavigationUrls} from '../types/navigation-urls-types'
import styles from './PullRequestHeaderNavigation.module.css'
import type {HeaderRepository} from '../page-data/payloads/header'
import type {PullRequest} from '../types'
import {pullRequestsCommitsRoute} from '../routes/commits-route'
import {pullRequestsChangesRoute} from '../routes/files-changed-routes'
import {pullRequestsConversationsRoute} from '../routes/conversations-route'
import {pullRequestsChecksRoute} from '../routes/checks-route'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {pullRequestsOverviewRoute} from '../routes/overview-route'
import type {PullRequestActiveRoute} from '../hooks/use-active-route'
import {ClientOnly} from './ClientOnly'

interface PullRequestHeaderNavigationProps {
  activeRoute: PullRequestActiveRoute
  pullRequest: PullRequest
  repository: HeaderRepository
  urls: NavigationUrls
}

export function PullRequestHeaderNavigation(props: PullRequestHeaderNavigationProps) {
  return (
    <ClientOnly serverFallback={<PullRequestHeaderNavigationShared {...props} labelCounts={undefined} />}>
      <PullRequestHeaderNavigationContent {...props} />
    </ClientOnly>
  )
}

export function PullRequestHeaderNavigationContent({
  activeRoute,
  pullRequest,
  repository,
  urls,
}: PullRequestHeaderNavigationProps) {
  const {data: labelCounts} = useRouteQuery(pullRequestsLayoutRoute, 'tabCounts')

  return (
    <PullRequestHeaderNavigationShared
      activeRoute={activeRoute}
      labelCounts={labelCounts}
      pullRequest={pullRequest}
      repository={repository}
      urls={urls}
    />
  )
}

function PullRequestHeaderNavigationShared({
  activeRoute,
  pullRequest,
  repository,
  urls,
  labelCounts,
}: PullRequestHeaderNavigationProps & {
  labelCounts: NavigationCounterPageData | undefined
}) {
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const isChangesEnabled = useFeatureFlag('react_data_router_pull_request_files')
  const isPreloadingEnabled = isFeatureEnabled('prs_preload_changes_route')
  const showUpdatedHeader = isFeatureEnabled('updated_pull_request_header')
  const tabClasses = `position-relative ${showUpdatedHeader ? '' : 'tmp-px-3 '}flex-shrink-0 text-normal ${styles.muteWhenUnselected} ${styles.overrideLineHeight}`

  const {commitsCount} = pullRequest

  const iconClasses = 'fg-muted mr-2 d-none d-sm-inline-block'

  const asyncCounterClasses = clsx('ml-2', labelCounts ? '' : styles.counterLoading)
  const walkthroughEnabled = isFeatureEnabled('hypersight')
  const overviewEnabled = isFeatureEnabled('prs_overview_react')

  if (!showUpdatedHeader) {
    return (
      <TabNav aria-label="Pull request navigation tabs" data-turbo="false">
        {isFeatureEnabled('prs_conversations_react') ? (
          <TabNav.Link
            as={Link}
            to={pullRequestsConversationsRoute.generatePath({
              owner: repository.ownerLogin,
              repo: repository.name,
              pr_number: pullRequest.number.toString(),
            })}
            selected={activeRoute === 'conversation'}
            className={tabClasses}
          >
            <CommentDiscussionIcon className={iconClasses} />
            Conversation
            {typeof labelCounts?.conversationCount === 'number' && (
              <CounterLabel className={asyncCounterClasses}>{labelCounts.conversationCount}</CounterLabel>
            )}
          </TabNav.Link>
        ) : (
          <TabNav.Link
            href={urls.conversation}
            selected={activeRoute === 'conversation'}
            className={tabClasses}
            data-prefetch="eager"
          >
            <CommentDiscussionIcon className={iconClasses} />
            Conversation
            {typeof labelCounts?.conversationCount === 'number' && (
              <CounterLabel className={asyncCounterClasses}>{labelCounts.conversationCount}</CounterLabel>
            )}
          </TabNav.Link>
        )}
        {overviewEnabled && (
          <TabNav.Link
            as={Link}
            to={pullRequestsOverviewRoute.generatePath({
              owner: repository.ownerLogin,
              repo: repository.name,
              pr_number: pullRequest.number.toString(),
            })}
            selected={activeRoute === 'overview'}
            className={tabClasses}
            data-prefetch="moderate"
          >
            <PulseIcon className={iconClasses} />
            Overview
          </TabNav.Link>
        )}
        {isFeatureEnabled('copilot_code_review_findings_ui') && (
          <TabNav.Link href={urls.findings} selected={activeRoute === 'findings'} className={tabClasses}>
            <ShieldIcon className={iconClasses} />
            Findings
          </TabNav.Link>
        )}
        <TabNav.Link
          as={Link}
          to={pullRequestsCommitsRoute.generatePath({
            owner: repository.ownerLogin,
            repo: repository.name,
            pr_number: pullRequest.number.toString(),
          })}
          selected={activeRoute === 'commits'}
          className={tabClasses}
          id="prs-commits-anchor-tab"
        >
          <GitCommitIcon className={iconClasses} />
          Commits
          {typeof commitsCount === 'number' && <CounterLabel className="ml-2">{commitsCount}</CounterLabel>}
        </TabNav.Link>
        {isFeatureEnabled('prs_checks_react') ? (
          <TabNav.Link
            as={Link}
            to={pullRequestsChecksRoute.generatePath({
              owner: repository.ownerLogin,
              repo: repository.name,
              pr_number: pullRequest.number.toString(),
            })}
            selected={activeRoute === 'checks'}
            className={tabClasses}
          >
            <ChecklistIcon className={iconClasses} />
            Checks
            {typeof labelCounts?.checksCount === 'number' && (
              <CounterLabel className={asyncCounterClasses}>{labelCounts.checksCount}</CounterLabel>
            )}
          </TabNav.Link>
        ) : (
          <TabNav.Link
            href={urls.checks}
            selected={activeRoute === 'checks'}
            className={tabClasses}
            data-prefetch="moderate"
          >
            <ChecklistIcon className={iconClasses} />
            Checks
            {typeof labelCounts?.checksCount === 'number' && (
              <CounterLabel className={asyncCounterClasses}>{labelCounts.checksCount}</CounterLabel>
            )}
          </TabNav.Link>
        )}
        {isChangesEnabled ? (
          <TabNav.Link
            as={Link}
            id="prs-files-anchor-tab"
            to={pullRequestsChangesRoute.generatePath({
              owner: repository.ownerLogin,
              repo: repository.name,
              pr_number: pullRequest.number.toString(),
            })}
            selected={activeRoute === 'files_changed'}
            className={tabClasses}
            preload={isPreloadingEnabled ? 'intent' : false}
          >
            <FileDiffIcon className={iconClasses} />
            Files changed
            {typeof labelCounts?.filesChangedCount === 'number' && (
              <CounterLabel className={asyncCounterClasses}>
                {`${labelCounts.filesChangedCount}${labelCounts.filesChangedCountLimitExceeded ? '+' : ''}`}
              </CounterLabel>
            )}
          </TabNav.Link>
        ) : (
          <TabNav.Link
            id="prs-files-anchor-tab"
            href={urls.files}
            selected={activeRoute === 'files_changed'}
            className={tabClasses}
          >
            <FileDiffIcon className={iconClasses} />
            Files changed
            {typeof labelCounts?.filesChangedCount === 'number' && (
              <CounterLabel className={asyncCounterClasses}>
                {`${labelCounts.filesChangedCount}${labelCounts.filesChangedCountLimitExceeded ? '+' : ''}`}
              </CounterLabel>
            )}
          </TabNav.Link>
        )}
        {walkthroughEnabled && (
          <TabNav.Link
            href={urls.walkthrough}
            selected={activeRoute === 'walkthrough'}
            className={tabClasses}
            data-prefetch="moderate"
          >
            <CopilotIcon className={iconClasses} />
            Walkthrough
          </TabNav.Link>
        )}
      </TabNav>
    )
  }

  return (
    <UnderlineNav aria-label="Pull request navigation tabs" className={'box-shadow-none'} data-turbo="false">
      {isFeatureEnabled('prs_conversations_react') ? (
        <UnderlineNav.Item
          as={Link}
          to={pullRequestsConversationsRoute.generatePath({
            owner: repository.ownerLogin,
            repo: repository.name,
            pr_number: pullRequest.number.toString(),
          })}
          aria-current={activeRoute === 'conversation' ? 'page' : undefined}
          className={tabClasses}
        >
          Conversation
          {typeof labelCounts?.conversationCount === 'number' && (
            <CounterLabel className={asyncCounterClasses}>{labelCounts.conversationCount}</CounterLabel>
          )}
        </UnderlineNav.Item>
      ) : (
        <UnderlineNav.Item
          href={urls.conversation}
          aria-current={activeRoute === 'conversation' ? 'page' : undefined}
          className={tabClasses}
          data-prefetch="eager"
        >
          Conversation
          {typeof labelCounts?.conversationCount === 'number' && (
            <CounterLabel className={asyncCounterClasses}>{labelCounts.conversationCount}</CounterLabel>
          )}
        </UnderlineNav.Item>
      )}
      {overviewEnabled && (
        <UnderlineNav.Item
          as={Link}
          to={pullRequestsOverviewRoute.generatePath({
            owner: repository.ownerLogin,
            repo: repository.name,
            pr_number: pullRequest.number.toString(),
          })}
          aria-current={activeRoute === 'overview' ? 'page' : undefined}
          className={tabClasses}
          data-prefetch="moderate"
        >
          Overview
        </UnderlineNav.Item>
      )}
      {isFeatureEnabled('copilot_code_review_findings_ui') && (
        <UnderlineNav.Item
          href={urls.findings}
          aria-current={activeRoute === 'findings' ? 'page' : undefined}
          className={tabClasses}
        >
          Findings
        </UnderlineNav.Item>
      )}
      <UnderlineNav.Item
        as={Link}
        to={pullRequestsCommitsRoute.generatePath({
          owner: repository.ownerLogin,
          repo: repository.name,
          pr_number: pullRequest.number.toString(),
        })}
        aria-current={activeRoute === 'commits' ? 'page' : undefined}
        className={tabClasses}
        id="prs-commits-anchor-tab"
      >
        Commits
        {typeof commitsCount === 'number' && <CounterLabel className="ml-2">{commitsCount}</CounterLabel>}
      </UnderlineNav.Item>
      {isFeatureEnabled('prs_checks_react') ? (
        <UnderlineNav.Item
          as={Link}
          to={pullRequestsChecksRoute.generatePath({
            owner: repository.ownerLogin,
            repo: repository.name,
            pr_number: pullRequest.number.toString(),
          })}
          aria-current={activeRoute === 'checks' ? 'page' : undefined}
          className={tabClasses}
        >
          Checks
          {typeof labelCounts?.checksCount === 'number' && (
            <CounterLabel className={asyncCounterClasses}>{labelCounts.checksCount}</CounterLabel>
          )}
        </UnderlineNav.Item>
      ) : (
        <UnderlineNav.Item
          href={urls.checks}
          aria-current={activeRoute === 'checks' ? 'page' : undefined}
          className={tabClasses}
          data-prefetch="moderate"
        >
          Checks
          {typeof labelCounts?.checksCount === 'number' && (
            <CounterLabel className={asyncCounterClasses}>{labelCounts.checksCount}</CounterLabel>
          )}
        </UnderlineNav.Item>
      )}
      {isChangesEnabled ? (
        <UnderlineNav.Item
          as={Link}
          id="prs-files-anchor-tab"
          to={pullRequestsChangesRoute.generatePath({
            owner: repository.ownerLogin,
            repo: repository.name,
            pr_number: pullRequest.number.toString(),
          })}
          aria-current={activeRoute === 'files_changed' ? 'page' : undefined}
          className={tabClasses}
          preload={isPreloadingEnabled ? 'intent' : false}
        >
          Files changed
          {typeof labelCounts?.filesChangedCount === 'number' && (
            <CounterLabel className={asyncCounterClasses}>
              {`${labelCounts.filesChangedCount}${labelCounts.filesChangedCountLimitExceeded ? '+' : ''}`}
            </CounterLabel>
          )}
        </UnderlineNav.Item>
      ) : (
        <UnderlineNav.Item
          id="prs-files-anchor-tab"
          href={urls.files}
          aria-current={activeRoute === 'files_changed' ? 'page' : undefined}
          className={tabClasses}
        >
          Files changed
          {typeof labelCounts?.filesChangedCount === 'number' && (
            <CounterLabel className={asyncCounterClasses}>
              {`${labelCounts.filesChangedCount}${labelCounts.filesChangedCountLimitExceeded ? '+' : ''}`}
            </CounterLabel>
          )}
        </UnderlineNav.Item>
      )}
      {walkthroughEnabled && (
        <UnderlineNav.Item
          href={urls.walkthrough}
          aria-current={activeRoute === 'walkthrough' ? 'page' : undefined}
          className={tabClasses}
          data-prefetch="moderate"
        >
          Walkthrough
        </UnderlineNav.Item>
      )}
    </UnderlineNav>
  )
}
